import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { EMPTY_VENICE_CONNECTION } from "../lib/venice";
import type { Session, VeniceConnection } from "../types";

export const VENICE_QUERY_KEY = ["venice-account"] as const;

export function useVeniceAccount(enabled = true) {
  const queryClient = useQueryClient();
  const updateCaches = (connection: VeniceConnection) => {
    queryClient.setQueryData(VENICE_QUERY_KEY, connection);
    queryClient.setQueryData<Session | null>(["session"], (session) => session
      ? { ...session, venice: { connected: connection.connected, status: connection.status } }
      : session);
  };
  const query = useQuery({
    queryKey: VENICE_QUERY_KEY,
    queryFn: api.veniceConnection,
    enabled,
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const connect = useMutation({ mutationFn: api.connectVenice, onSuccess: updateCaches });
  const refresh = useMutation({ mutationFn: api.refreshVenice, onSuccess: updateCaches });
  const disconnect = useMutation({
    mutationFn: api.disconnectVenice,
    onSuccess: () => updateCaches(EMPTY_VENICE_CONNECTION),
  });
  return { ...query, connect, refresh, disconnect };
}

export function useRefreshVeniceBalance() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: VENICE_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ["session"] }),
    ]);
  };
}
